import styles from "./styles/InputField.module.css";
import cn from "classnames";
import { Warning as IconWarning } from "phosphor-react";

import { ReactNode, useMemo } from "react";
import { uniqueId } from "lodash";
import { Label } from "./Label";

type Size = "S" | "M" | "L";

type Style = "contained" | "outlined";

export interface FormFieldProps {
  size: Size;
  style: Style;
  renderInput: (props: { id: string; disabled?: boolean }) => ReactNode;
  id?: string;
  label?: ReactNode; // This can come with only text, but with an icon as well
  disabled?: boolean;
  hint?: ReactNode; // This can come with only text, but with an icon as well
  error?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  inputWrapperClassName?: string;
  className?: string;
}

export const FormField = (props: FormFieldProps) => {
  const inputId = useMemo(() => props.id ?? uniqueId("inputId-"), [props.id]);

  const {
    size,
    style,
    label,
    hint,
    disabled = false,
    error,
    prefix,
    suffix,
    inputWrapperClassName,
  } = props;

  return (
    <div className={props.className}>
      {label !== undefined && <Label htmlFor={inputId}>{label}</Label>}
      <div
        className={cn(
          styles.input,
          styles[`size-${size}`],
          styles[`style-${style}`],
          disabled && styles.disabled,
          error !== undefined && styles.inputError,
          inputWrapperClassName
        )}
      >
        {prefix !== undefined && (
          <span className={styles.prefix}>{prefix}</span>
        )}
        {props.renderInput({ id: inputId, disabled })}
        {suffix !== undefined && (
          <span className={styles.suffix}>{suffix}</span>
        )}
      </div>
      {error !== undefined && (
        <div className={styles.error} role="alert">
          <IconWarning />
          {error}
        </div>
      )}
      {hint !== undefined && (
        <div className={styles.hint} aria-describedby={inputId}>
          {hint}
        </div>
      )}
    </div>
  );
};
